import React, { useState, useEffect, useRef } from 'react';
import audioManager from '../../filemanager/AudioManager';
import { PUZZLE_TYPES } from './config';
import './PuzzleDisplay.css';
import accountStore from '../../../components/account/AccountStore';
import PlayerService from '../../../services/PlayerService';
const PuzzleDisplay = ({ puzzle, onComplete, onClose }) => {
  const [attempt, setAttempt] = useState(() => {
    if (!puzzle?.initial) return null;
    return JSON.parse(JSON.stringify(puzzle.initial));
  });
  const [timeRemaining, setTimeRemaining] = useState(puzzle.timeLimit);
  const [successData, setSuccessData] = useState(null);
  const [progress, setProgress] = useState(0);

  const timerRef = useRef(null);
  const rewardsGrantedRef = useRef(false); // Guard to prevent reward spamming

  // Calculate progress percentage
  const calculateProgress = () => {
    if (!attempt || !puzzle?.solution) return 0;

    const totalCells = 9; // 3x3 grid
    let correctCells = 0;
    
    for (let i = 0; i < 3; i++) {
      for (let j = 0; j < 3; j++) {
        if (attempt[i][j] === puzzle.solution[i][j]) {
          correctCells++;
        }
      }
    }
    
    return Math.floor((correctCells * 100) / totalCells);
  };

  // Simple countdown
  useEffect(() => {
    timerRef.current = setInterval(() => {
      setTimeRemaining(time => {
        if (time <= 1) {
          if (timerRef.current) {
            clearInterval(timerRef.current);
          }
          onComplete({
            qubits: 0,
            multiplier: 0,
            message: '[PUZZLE FAILED - TIME EXPIRED]'
          });
          return 0;
        }
        return time - 1;
      });
    }, 1000);

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, []);

  // Check for solution completion
  useEffect(() => {
    if (attempt) {
      const newProgress = calculateProgress();
      setProgress(newProgress);

      // Guard: Only grant rewards once per puzzle instance
      if (newProgress === 100 && !rewardsGrantedRef.current) {
        // Set guard immediately to prevent duplicate rewards
        rewardsGrantedRef.current = true;

        // Play success sound
        audioManager.playSuccess();

        // Stop the timer
        if (timerRef.current) {
          clearInterval(timerRef.current);
        }

        const rewards = puzzle.calculateRewards(timeRemaining);
        const currentQubits = accountStore.getQubits();

        // Update local account store
        accountStore.setProfile({
          ...accountStore.getProfile(),
          data: {
            ...accountStore.getProfile().data,
            qubits: currentQubits + rewards.qubits
          }
        });

        // Update Firebase
        try {
          const profile = accountStore.getProfile();
          if (profile?.playerId) {
            PlayerService.updatePlayerData(
              profile.playerId,
              { qubits: rewards.qubits },
              { increment: true }
            ).catch(console.error);
          }
        } catch (error) {
          console.error('Error updating Firebase:', error);
        }

        setSuccessData(rewards);

        // Auto close after reward popup animation
        setTimeout(() => {
          onComplete(rewards);
        }, 1500);
      }
    }
  }, [attempt]);

  const renderBinaryGrid = () => (
    <div className="puzzle-binary">
      <div className="binary-grid">
        {puzzle.initial.map((row, i) => (
          <div key={i} className="binary-row">
            {row.map((cell, j) => {
              const isCorrect = attempt[i][j] === puzzle.solution[i][j];
              return (
                <button
                  key={j}
                  className={`binary-cell ${isCorrect ? 'correct' : 'incorrect'}`}
                  onClick={() => {
                    const newAttempt = JSON.parse(JSON.stringify(attempt));
                    newAttempt[i][j] = newAttempt[i][j] === 1 ? 0 : 1;
                    setAttempt(newAttempt);
                  }}
                >
                  {attempt[i][j]}
                </button>
              );
            })}
          </div>
        ))}
      </div>
    </div>
  );

  return (
    <div className="puzzle-overlay">
      <div className="puzzle-container">
        <h3>QUANTUM ENCRYPTION PUZZLE</h3>
        <div className="puzzle-progress-section">
          <div className={`puzzle-timer ${
            timeRemaining <= 5 ? 'critical' :
            timeRemaining <= 10 ? 'warning' : ''
          }`}>
            Time Remaining: {timeRemaining}s
          </div>
          <div className="progress-bar">
            <div 
              className="progress-fill"
              style={{ width: `${progress}%` }}
            />
            <div className="progress-text">
              {progress}% Complete
            </div>
          </div>
        </div>
        {renderBinaryGrid()}
      </div>
      {successData && (
        <div className="reward-popup">
          +{successData.qubits} QUBITS
        </div>
      )}
    </div>
  );
};

export default PuzzleDisplay;